import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnDestroy } from '@angular/core';
import { AbstractDynamicComponent } from '@components/abstracts';
import { AvisGoogleDetails } from '@components/avis-google-details/models';
import { TranslatePipe } from '@ngx-translate/core';
import { AppRatingDistributionComponent } from '@shared/components/app-rating-distribution';
import { ReviewBadgeComponent } from '@shared/components/review-badge';
import { ReviewStats } from '@shared/components/review-badge/models';
import { ReviewListComponent } from '@shared/components/review-list';
import { ReviewLoadingMode } from '@shared/components/review-list/models';
import { TitleComponent } from '@shared/components/title';
import { CurrentLanguageService } from '@shared/services/language';
import { SeoService } from '@shared/services/seo';
import { JsonLD } from '@shared/services/seo/models';
import { envConfiguredHostname } from '@shared/utils';

@Component({
  selector: 'app-avis-google-details',
  imports: [
    TitleComponent,
    ReviewBadgeComponent,
    NgClass,
    AppRatingDistributionComponent,
    TranslatePipe,
    ReviewListComponent,
  ],
  templateUrl: './avis-google-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvisGoogleDetailsComponent extends AbstractDynamicComponent<AvisGoogleDetails> implements OnDestroy {
  readonly popinReviewDetails = input<AvisGoogleDetails>();

  readonly displayedInPopin = computed(() => !!this.popinReviewDetails());
  readonly loadMoreReviewsMode = computed<ReviewLoadingMode>(() => (this.displayedInPopin() ? 'scroll' : 'button'));
  readonly isStorePage = computed(() => !!this.data?.storeId);

  private readonly seoService = inject(SeoService);
  private readonly language = inject(CurrentLanguageService).language;

  constructor() {
    super();
    effect(() => {
      if (this.displayedInPopin()) this.data = this.popinReviewDetails() as AvisGoogleDetails;

      if (!this.data?.storeId && this.data?.reviewStats) {
        const schema = this.computeJsonLdSchema(this.data.reviewStats);
        this.seoService.addJsonLdScript('review', schema);
      }
    });
  }

  ngOnDestroy(): void {
    this.seoService.removeJsonLdScript('review');
  }

  private computeJsonLdSchema(reviewStats: ReviewStats): JsonLD {
    return {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: "SoCoo'c",
      url: envConfiguredHostname(this.language.region),
      ...(reviewStats.totalReviews >= 1
        ? {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: reviewStats.averageRating || 0,
              ratingCount: reviewStats.totalReviews || 0,
            },
          }
        : {}),
    };
  }
}
